import { createElement, useState, type SVGProps } from "react";
import { ProviderDriverKind } from "@t3tools/contracts";
import { ClaudeAI, CursorIcon, GrokIcon, Icon, OpenAI, OpenCodeIcon } from "../Icons";
import { PROVIDER_OPTIONS } from "../../session-logic";
import { cn } from "~/lib/utils";

export type { Icon } from "../Icons";

// --- Real brand logos via Simple Icons CDN ----------------------------------
// `https://cdn.simpleicons.org/<slug>` returns a real, colored brand SVG for
// most big providers. Some slugs 404 (openai, cohere, groq, …) — those must
// gracefully fall back to the provider's existing monogram/brand `Icon`.

function simpleIconUrl(slug: string): string {
  return `https://cdn.simpleicons.org/${slug}`;
}

/**
 * Renders a real brand logo from the Simple Icons CDN, falling back to the
 * provider's existing monogram/brand `Icon` if the slug 404s (or the image
 * otherwise fails to load). Self-contained: keeps its own error state so a
 * broken logo swaps to the fallback exactly once.
 */
export function ProviderLogo({
  slug,
  name,
  fallback: Fallback,
  className,
}: {
  /** Simple Icons slug. When omitted, renders the fallback directly. */
  slug?: string | undefined;
  /** Accessible name / alt text. */
  name: string;
  /** Fallback icon shown if the logo fails to load or no slug is given. */
  fallback: Icon;
  /** Sizing class applied to both the logo and the fallback (e.g. "size-5"). */
  className?: string | undefined;
}) {
  const [failed, setFailed] = useState(false);
  if (!slug || failed) {
    return createElement(Fallback, { className: cn("shrink-0", className), "aria-hidden": true });
  }
  return createElement("img", {
    src: simpleIconUrl(slug),
    alt: name,
    className: cn("object-contain shrink-0", className),
    loading: "lazy",
    onError: () => setFailed(true),
  });
}

export const PROVIDER_ICON_BY_PROVIDER: Partial<Record<ProviderDriverKind, Icon>> = {
  [ProviderDriverKind.make("codex")]: OpenAI,
  [ProviderDriverKind.make("claudeAgent")]: ClaudeAI,
  [ProviderDriverKind.make("opencode")]: OpenCodeIcon,
  [ProviderDriverKind.make("cursor")]: CursorIcon,
  [ProviderDriverKind.make("grok")]: GrokIcon,
};

function isAvailableProviderOption(option: (typeof PROVIDER_OPTIONS)[number]): option is {
  value: ProviderDriverKind;
  label: string;
  available: true;
  pickerSidebarBadge?: "new" | "soon";
} {
  return option.available;
}

export const AVAILABLE_PROVIDER_OPTIONS = PROVIDER_OPTIONS.filter(isAvailableProviderOption);

export type ModelEsque = {
  slug: string;
  name: string;
  shortName?: string | undefined;
  subProvider?: string | undefined;
  isLegacy?: boolean | undefined;
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripLeadingQualifier(value: string, qualifier: string | null | undefined): string {
  const trimmedQualifier = qualifier?.trim();
  if (!trimmedQualifier) {
    return value;
  }

  const pattern = new RegExp(`^${escapeRegExp(trimmedQualifier)}(?:\\s*[.:/-]\\s*|\\s+)`, "iu");
  return value.replace(pattern, "").trim() || value;
}

export function getDisplayModelName(
  model: ModelEsque,
  options?: { preferShortName?: boolean },
): string {
  const name = options?.preferShortName && model.shortName ? model.shortName : model.name;
  return stripLeadingQualifier(name, model.subProvider);
}

export function getTriggerDisplayModelName(model: ModelEsque): string {
  return getDisplayModelName(model, { preferShortName: true });
}

export function getTriggerDisplayModelLabel(model: ModelEsque): string {
  return getTriggerDisplayModelName(model);
}

// --- Preview-provider glyphs -------------------------------------------------
// Clean monogram glyphs for the curated showcase catalog providers that don't
// ship a full brand SVG in Icons.tsx. Each is a rounded tile in the company's
// brand color with a legible letter mark — not a fake replica of the real
// logo, just a recognizable, consistent monogram. Rendered without JSX because
// this is a plain `.ts` module.

/**
 * Build a monogram tile: a filled rounded square in the brand color with a
 * centered glyph in the tile's contrast color. Font size auto-scales for
 * multi-character marks (e.g. "AI", "Z.AI") so they stay inside the tile.
 */
export function makeMonogramIcon(
  glyph: string,
  brandClassName: string,
  options?: { foreground?: string; color?: string },
): Icon {
  const fontSize = glyph.length >= 3 ? 8.5 : glyph.length === 2 ? 10.5 : 13;
  const foreground = options?.foreground ?? "#fff";
  // When a literal `color` is provided we drive the tile fill via an inline
  // `color` (currentColor) instead of a Tailwind class. This is required for
  // runtime-generated brand colors, which Tailwind's static JIT can't see.
  const inlineColor = options?.color;
  const MonogramGlyph: Icon = ({ className, style, ...props }: SVGProps<SVGSVGElement>) =>
    createElement(
      "svg",
      {
        ...props,
        viewBox: "0 0 24 24",
        className: cn(brandClassName, className),
        style: inlineColor ? { color: inlineColor, ...style } : style,
      },
      createElement("rect", { x: 1, y: 1, width: 22, height: 22, rx: 5, fill: "currentColor" }),
      createElement(
        "text",
        {
          x: 12,
          y: 12,
          textAnchor: "middle",
          dominantBaseline: "central",
          fontSize,
          fontWeight: 700,
          letterSpacing: glyph.length >= 2 ? -0.3 : 0,
          fontFamily: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          fill: foreground,
        },
        glyph,
      ),
    );
  return MonogramGlyph;
}

export const DeepSeekGlyph: Icon = makeMonogramIcon("DS", "text-[#4d6bfe]");
export const MetaGlyph: Icon = makeMonogramIcon("M", "text-[#0866ff]");
export const CloudflareGlyph: Icon = makeMonogramIcon("CF", "text-[#f38020]");
export const MistralGlyph: Icon = makeMonogramIcon("ML", "text-[#fa5310]");
export const QwenGlyph: Icon = makeMonogramIcon("Q", "text-[#615ced]");
export const MoonshotGlyph: Icon = makeMonogramIcon("K", "text-[#111319]");
export const ZaiGlyph: Icon = makeMonogramIcon("Z", "text-[#2f6df6]");
export const NvidiaGlyph: Icon = makeMonogramIcon("N", "text-[#76b900]");
export const IbmGlyph: Icon = makeMonogramIcon("IBM", "text-[#0f62fe]");
export const AiSingaporeGlyph: Icon = makeMonogramIcon("AIS", "text-[#d7263d]");
export const AmazonGlyph: Icon = makeMonogramIcon("A", "text-[#ff9900]");

// --- Cost / capability presentation metadata --------------------------------

export type ModelCostTier = "free" | "cheap" | "standard" | "premium" | "pro";

export type CostBadgeMeta = {
  /** Short label shown in the badge ("$", "$$$", "Free", "Pro"). */
  label: string;
  /** Tailwind color classes (border + text) for the chip. */
  className: string;
  /** Accessible description. */
  title: string;
};

export const COST_BADGE_META: Record<ModelCostTier, CostBadgeMeta> = {
  free: {
    label: "Free",
    className: "border-emerald-500/60 text-emerald-600 dark:text-emerald-400",
    title: "Free",
  },
  cheap: {
    label: "$",
    className: "border-emerald-500/60 text-emerald-600 dark:text-emerald-400",
    title: "Low cost",
  },
  standard: {
    label: "$$",
    className: "border-amber-500/60 text-amber-600 dark:text-amber-400",
    title: "Standard cost",
  },
  premium: {
    label: "$$$",
    className: "border-primary/70 text-primary",
    title: "Premium cost",
  },
  pro: {
    label: "Pro",
    className: "border-primary/70 text-primary",
    title: "Pro plan",
  },
};

// --- Model tier presentation metadata ---------------------------------------
// Three-tier system for the Morrow catalog: free (green), pay (amber),
// super (red). Rendered as a colored badge on each chat model row. Utility
// (background) models carry no tier — they show a dim "bg" badge instead.

export type ModelTier = "free" | "pay" | "super";

export type TierBadgeMeta = {
  /** Short label shown in the badge. */
  label: string;
  /** Tailwind color classes (border + text) for the chip. */
  className: string;
  /** Accessible description. */
  title: string;
};

export const TIER_BADGE_META: Record<ModelTier, TierBadgeMeta> = {
  free: {
    label: "Free",
    className: "border-emerald-500/60 text-emerald-600 dark:text-emerald-400",
    title: "Free tier",
  },
  pay: {
    label: "Pay",
    className: "border-amber-500/70 text-amber-600 dark:text-amber-400",
    title: "Paid tier",
  },
  super: {
    label: "Super",
    className: "border-primary/70 text-primary",
    title: "Super (premium) tier",
  },
};

/** Badge shown on utility/background rows (no user-facing tier). */
export const UTILITY_BADGE_META = {
  label: "bg",
  className: "border-border/60 text-muted-foreground/70",
  title: "Runs automatically in the background",
} as const;
